import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  ExternalLink,
  Settings,
  Cloud,
  Key,
  Mail,
  Webhook,
  Copy,
  Check,
  ChevronRight,
  Server,
  UserPlus,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function PartnerCustomers() {
  const { partner } = useOutletContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  
  // New customer form
  const [newCustomer, setNewCustomer] = useState({
    company_name: '',
    account_number: '',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    plan: 'pro',
    sip_domain: 'securevoip.nz',
    provider: 'hero',
    api_base_url: 'https://admin1.connect-us.co.nz',
    api_username: '',
    api_password: '',
    billing_email: '',
    contacts_url: ''
  });

  // Creation result (webhook info)
  const [creationResult, setCreationResult] = useState(null);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Provider presets
  const PROVIDER_PRESETS = {
    hero: { label: 'Hero / Connect-US', api_base_url: 'https://admin1.connect-us.co.nz', sip_domain: 'securevoip.nz' },
    '2talk': { label: '2Talk', api_base_url: '', sip_domain: '' },
    other: { label: 'Other', api_base_url: '', sip_domain: '' }
  };

  const handleProviderChange = (provider) => {
    const preset = PROVIDER_PRESETS[provider] || {};
    setNewCustomer(prev => ({
      ...prev,
      provider,
      api_base_url: preset.api_base_url || '',
      sip_domain: preset.sip_domain || ''
    }));
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      if (!token) {
        navigate('/partner/login');
        return;
      }
      const response = await axios.get(`${API_URL}/api/partner/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load customers:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        // Don't auto-redirect - let user manually go to login
      } else {
        toast.error('Failed to load customers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    
    try {
      const token = localStorage.getItem('partner_token');
      const response = await axios.post(`${API_URL}/api/partner/customers`, newCustomer, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Customer created successfully!');
      setCreationResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setAddLoading(false);
    }
  };

  const resetAndCloseDialog = () => {
    setShowAddDialog(false);
    setCreationResult(null);
    setShowPasswordField(false);
    setCopiedUrl(false);
    setCopiedSecret(false);
    setNewCustomer({
      company_name: '',
      account_number: '',
      admin_name: '',
      admin_email: '',
      admin_phone: '',
      admin_password: '',
      plan: 'pro',
      sip_domain: 'securevoip.nz',
      provider: 'hero',
      api_base_url: 'https://admin1.connect-us.co.nz',
      api_username: '',
      api_password: '',
      billing_email: '',
      contacts_url: ''
    });
    fetchCustomers();
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'url') { setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); }
    if (type === 'secret') { setCopiedSecret(true); setTimeout(() => setCopiedSecret(false), 2000); }
  };

  const viewCustomerDetail = async (customerId) => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await axios.get(`${API_URL}/api/partner/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCustomer(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.account_number?.includes(searchTerm)
  );

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'pro': return 'bg-cyan-500';
      default: return 'bg-gray-500';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Customers</h1>
          <p className="text-muted-foreground mt-1">
            Manage your referred customers and subscriptions
          </p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) resetAndCloseDialog(); else setShowAddDialog(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {creationResult ? (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-500" />
                    Customer Created
                  </DialogTitle>
                  <DialogDescription>
                    {creationResult.customer?.name} has been set up successfully. Configure the webhook below in their PBX.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Card className="border-emerald-500/30 bg-emerald-500/5">
                    <CardContent className="pt-4 space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Company</Label>
                        <p className="font-medium">{creationResult.customer?.name}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground">Plan</Label>
                          <p className="font-medium capitalize">{creationResult.customer?.plan}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Admin SIP Username</Label>
                          <p className="font-mono text-sm">{creationResult.customer?.admin_phone}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Separator />

                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-3">
                      <Webhook className="w-4 h-4 text-primary" />
                      Webhook Configuration
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Enter these details in the customer's PBX webhook settings to receive real-time call events.
                    </p>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Webhook URL</Label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 p-2 bg-secondary/50 rounded text-xs font-mono border border-border/50 truncate" data-testid="webhook-url-result">
                            {creationResult.webhook?.url}
                          </code>
                          <Button variant="outline" size="sm" onClick={() => copyToClipboard(creationResult.webhook?.url, 'url')}>
                            {copiedUrl ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Webhook Secret</Label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 p-2 bg-secondary/50 rounded text-xs font-mono border border-border/50" data-testid="webhook-secret-result">
                            {creationResult.webhook?.secret}
                          </code>
                          <Button variant="outline" size="sm" onClick={() => copyToClipboard(creationResult.webhook?.secret, 'secret')}>
                            {copiedSecret ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={resetAndCloseDialog} data-testid="done-btn">Done</Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Add New Customer</DialogTitle>
                  <DialogDescription>
                    Set up a new customer with their PBX provider, API credentials, and admin account.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddCustomer}>
                  <div className="space-y-5 py-4">
                    {/* Section 1: Company Details */}
                    <div>
                      <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                        <Building2 className="w-4 h-4 text-primary" />
                        Company Details
                      </h4>
                      <div className="grid gap-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="company_name" className="text-xs">Company Name *</Label>
                            <Input
                              id="company_name"
                              placeholder="ABC Company Ltd"
                              value={newCustomer.company_name}
                              onChange={(e) => setNewCustomer({...newCustomer, company_name: e.target.value})}
                              required
                              data-testid="input-company-name"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="account_number" className="text-xs">Account Number *</Label>
                            <Input
                              id="account_number"
                              placeholder="83020001"
                              value={newCustomer.account_number}
                              onChange={(e) => setNewCustomer({...newCustomer, account_number: e.target.value})}
                              required
                              data-testid="input-account-number"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Subscription Plan *</Label>
                            <Select 
                              value={newCustomer.plan} 
                              onValueChange={(value) => setNewCustomer({...newCustomer, plan: value})}
                            >
                              <SelectTrigger data-testid="select-plan">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">Free - $0/user/month</SelectItem>
                                <SelectItem value="pro">Pro - $20/user/month</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="billing_email" className="text-xs">Billing Email</Label>
                            <Input
                              id="billing_email"
                              type="email"
                              placeholder="billing@company.com"
                              value={newCustomer.billing_email}
                              onChange={(e) => setNewCustomer({...newCustomer, billing_email: e.target.value})}
                              data-testid="input-billing-email"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Section 2: PBX Provider & API */}
                    <div>
                      <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                        <Cloud className="w-4 h-4 text-primary" />
                        Cloud PBX Provider
                      </h4>
                      <div className="grid gap-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Provider *</Label>
                            <Select 
                              value={newCustomer.provider} 
                              onValueChange={handleProviderChange}
                            >
                              <SelectTrigger data-testid="select-provider">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="hero">Hero / Connect-US</SelectItem>
                                <SelectItem value="2talk">2Talk</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="sip_domain" className="text-xs">SIP Domain</Label>
                            <Input
                              id="sip_domain"
                              placeholder="securevoip.nz"
                              value={newCustomer.sip_domain}
                              onChange={(e) => setNewCustomer({...newCustomer, sip_domain: e.target.value})}
                              data-testid="input-sip-domain"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="api_base_url" className="text-xs">API Base URL</Label>
                          <Input
                            id="api_base_url"
                            placeholder="https://admin1.connect-us.co.nz"
                            value={newCustomer.api_base_url}
                            onChange={(e) => setNewCustomer({...newCustomer, api_base_url: e.target.value})}
                            data-testid="input-api-base-url"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="contacts_url" className="text-xs">Contacts Sync URL</Label>
                          <Input
                            id="contacts_url"
                            placeholder="https://portal1.connect-us.co.nz/api/contacts.php?l=..."
                            value={newCustomer.contacts_url}
                            onChange={(e) => setNewCustomer({...newCustomer, contacts_url: e.target.value})}
                            data-testid="input-contacts-url"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="api_username" className="text-xs">API Username</Label>
                            <Input
                              id="api_username"
                              placeholder="api@company.com"
                              value={newCustomer.api_username}
                              onChange={(e) => setNewCustomer({...newCustomer, api_username: e.target.value})}
                              data-testid="input-api-username"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="api_password" className="text-xs">API Password</Label>
                            <Input
                              id="api_password"
                              type={showPasswordField ? "text" : "password"}
                              placeholder="API password"
                              value={newCustomer.api_password}
                              onChange={(e) => setNewCustomer({...newCustomer, api_password: e.target.value})}
                              data-testid="input-api-password"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Section 3: Admin User */}
                    <div>
                      <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                        <UserPlus className="w-4 h-4 text-primary" />
                        Admin User
                      </h4>
                      <div className="grid gap-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="admin_name" className="text-xs">Name *</Label>
                            <Input
                              id="admin_name"
                              placeholder="John Smith"
                              value={newCustomer.admin_name}
                              onChange={(e) => setNewCustomer({...newCustomer, admin_name: e.target.value})}
                              required
                              data-testid="input-admin-name"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="admin_email" className="text-xs">Email *</Label>
                            <Input
                              id="admin_email"
                              type="email"
                              placeholder="john@company.com"
                              value={newCustomer.admin_email}
                              onChange={(e) => setNewCustomer({...newCustomer, admin_email: e.target.value})}
                              required
                              data-testid="input-admin-email"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="admin_phone" className="text-xs">SIP Username *</Label>
                            <Input
                              id="admin_phone"
                              placeholder="830200010001"
                              value={newCustomer.admin_phone}
                              onChange={(e) => setNewCustomer({...newCustomer, admin_phone: e.target.value})}
                              required
                              data-testid="input-admin-phone"
                            />
                            <p className="text-[10px] text-muted-foreground">12-digit PBX phone number</p>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="admin_password" className="text-xs">SIP Password *</Label>
                            <Input
                              id="admin_password"
                              type="password"
                              placeholder="SIP password"
                              value={newCustomer.admin_password}
                              onChange={(e) => setNewCustomer({...newCustomer, admin_password: e.target.value})}
                              required
                              data-testid="input-admin-password"
                            />
                            <p className="text-[10px] text-muted-foreground">From PBX config</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={resetAndCloseDialog}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={addLoading} data-testid="create-customer-btn">
                      {addLoading ? 'Creating...' : 'Create Customer'}
                    </Button>
                  </DialogFooter>
                </form>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Customers ({total})</CardTitle>
          <CardDescription>Companies you've referred to CONNECT</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredCustomers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Account #</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-primary" />
                        </div>
                        <span className="font-medium">{customer.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{customer.account_number}</TableCell>
                    <TableCell>
                      <Badge className={`${getPlanBadgeColor(customer.plan)} capitalize`}>
                        {customer.plan}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        {customer.user_count}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={customer.subscription_status === 'active' ? 'default' : 'secondary'}
                        className={customer.subscription_status === 'active' ? 'bg-green-500' : ''}
                      >
                        {customer.subscription_status || 'trial'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(customer.created_at)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/partner/customers/${customer.id}`)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Manage
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => viewCustomerDetail(customer.id)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-2">No customers yet</h3>
              <p className="text-muted-foreground mb-4">
                Add your first customer or share your referral link
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCustomer?.name}</DialogTitle>
            <DialogDescription>Customer details and subscription info</DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Account Number</Label>
                  <p className="font-mono">{selectedCustomer.account_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Plan</Label>
                  <p className="capitalize">{selectedCustomer.plan}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Users</Label>
                  <p>{selectedCustomer.user_count}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p className="capitalize">{selectedCustomer.subscription_status || 'trial'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created</Label>
                  <p>{formatDate(selectedCustomer.created_at)}</p>
                </div>
                {selectedCustomer.subscription_started_at && (
                  <div>
                    <Label className="text-muted-foreground">Subscription Started</Label>
                    <p>{formatDate(selectedCustomer.subscription_started_at)}</p>
                  </div>
                )}
              </div>
              
              {selectedCustomer.plan_details && (
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground">Wholesale Cost</Label>
                  <p>${selectedCustomer.plan_details.wholesale_price_monthly || selectedCustomer.plan_details.retail_price_monthly || selectedCustomer.plan_details.price_monthly || 0}/user/month</p>
                  {(selectedCustomer.plan_details.retail_price_monthly || selectedCustomer.plan_details.price_monthly) > 0 && (
                    <p className="text-sm text-muted-foreground">
                      RRP: ${selectedCustomer.plan_details.retail_price_monthly || selectedCustomer.plan_details.price_monthly}/user/month
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
